import { Component, Input, ChangeDetectorRef, OnChanges, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { cloneDeep, isEqual } from 'lodash';
import { NotificationService, ErrorLoggerService } from 'app/services';
import { AdminEditorService } from '../../admin.service';
import { ContactsSectionHandler, ContactsSectionValues } from '../../entities/shared/contacts.section';
import { Contact } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { ERRORS } from 'app/shared/model/errors.model';

@Component({
    selector: 'key-admin-contact-editor',
    templateUrl: './contact-editor.component.html',
    styleUrls: ['./contact-editor.component.scss'],
})
export class AdminContactEditorComponent implements OnChanges {

    @Input() entity: any;
    @Input() handler: ContactsSectionHandler;
    @Input() editable: boolean;

    @Output() onEditing = new EventEmitter<boolean>();
    @Output() onSaving = new EventEmitter<any>();
    @Output() onSaved = new EventEmitter<any>();

    errorMessage: string;

    errors: { [field: string]: string } = {};

    originalValues: ContactsSectionValues;
    contacts: Contact[];

    actions = [{ id: 'add', name: this.i18n.instant('SHARED.ADD') + '...' }];

    private _editing: boolean;
    get editing() { return this._editing; }
    set editing(value: boolean) {
        this._editing = value;
        this.onEditing.emit(value);
    }

    get saveable() {
        return this.originalValues && !isEqual(this.originalValues.contacts, this.contacts);
    }

    constructor(
        private admin: AdminEditorService,
        private error: ErrorLoggerService,
        private notify: NotificationService,
        public i18n: TranslateService,
        private ref: ChangeDetectorRef
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.handler && this.handler) {
            this.load();
        }
    }

    async load() {
        this.errorMessage = null;
        this.errors = {};
        const values = this.entity ? await this.handler.mapEntityToEditor(this.entity) : { contacts: [] };
        this.originalValues = values;
        this.contacts = cloneDeep(this.originalValues.contacts);
        this.ref.markForCheck();
    }

    onStartEdit() {
        this.errorMessage = null;
        this.errors = {};
        this.editing = !this.editing;
        this.ref.markForCheck();
    }

    showError(error: any) {
        if (error && error.name === ERRORS.VALIDATE_ERROR && error.data && error.data.fields) {
            this.errors = Object.keys(error.data.fields).reduce((result, field) => {
                result[field] = error.data.fields[field].message;
                return result;
            }, {});
            console.log('validationErrors', this.errors);
        } else {
            this.error.trackException(error);
            this.errorMessage = error && this.notify.translateError(error);
        }
        this.ref.markForCheck();

    }

    onAction(action: string) {
        if (action === 'add') {
            this.handler.showContactModal(this.contacts, null).then(() => {
                this.ref.markForCheck();
            });
        }
    }

    onEdit(contact: Contact) {
        if (this.editing) {
            this.handler.showContactModal(this.contacts, contact).then(() => {
                this.ref.markForCheck();
            });
        }
    }

    onDelete(contact: Contact) {
        const idx = this.contacts.indexOf(contact);
        this.contacts.splice(idx, 1);
    }

    onCancel() {
        this.errorMessage = null;
        this.errors = {};
        this.editing = false;
        this.contacts = cloneDeep(this.originalValues.contacts);
        this.ref.markForCheck();
    }

    onSave() {
        this.errorMessage = null;
        this.errors = {};
        this.onSaving.emit();

        this.handler.mapEditorToEntity({}, { contacts: this.contacts }).then(updatePayload => {
            if (this.entity) {
                return this.handler.applyUpdate(this.entity, updatePayload).then(item => {
                    this.editing = false;
                    this.entity = item;
                    this.load();
                    this.onSaved.emit(item);
                });
            } else {
                // this is a bulk update scenario
                this.onCancel();
                this.onSaved.emit(null);
                const items = [...this.admin.selectedItems]; // clone them so nothing can change them underneath us
                const changeList = [{ name: this.i18n.instant('ADMIN.ENTITIES.CONTACTS'), value: this.i18n.instant('ADMIN.ENTITIES.CONTACTS_COUNT', { count: this.contacts.length }) }];
                return this.admin.confirmBulkUpdates(items.length, changeList).then(result => {
                    if (result) {
                        return this.admin.applyBulkUpdates(this.handler.applyUpdate.bind(this.handler), items, updatePayload);
                    }
                });
            }
        }).catch(err => {
            this.showError(err);
            this.onSaved.emit(err);
        });


        this.ref.markForCheck();
    }




}
